<template>
    <div class="comment-block">
        <hr v-show="isReply" />
                
        <div class="row" :class="{'inital-comment': !isReply}">
            <div class="col-12 col-sm-auto">
                <div class="avatar mx-auto" :class="{'avatar-sm d-none d-sm-block': isReply}">
                    <img v-if="comment.commentator.avatar_thumbnail" :src="comment.commentator.avatar_thumbnail" alt="" />
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 192 192">
                        <path fill="#737270" d="M176.2,148.775a96.023,96.023,0,0,1-160.443-.052c6.809-13.478,22.857-22.22,46.68-26.163a53.485,53.485,0,0,0,67.35.1C153.527,126.647,169.419,135.34,176.2,148.775Z"/>
                        <circle fill="#737270" fill-rule="evenodd" cx="96" cy="81.234" r="47.375"/>
                    </svg>
                </div>
            </div>
            <div class="col mt-2">
                <div class="comment-headline">
                    #{{ identifier }} 
                    <strong>{{ comment.commentator.display_name }}</strong> 
                    <span class="badge badge-info" style="vertical-align: text-top;">{{ comment.badge }}</span> 
                    <small class="comment-date">am {{ comment.created_at | formatDate }}</small>
                </div>
                <p class="pt-2" style="white-space: pre-wrap;">{{ comment.comment }}</p>
            </div>
        </div>

        <div class="row">
            <div v-if="!isReply" class="col-auto comment-indent d-none d-sm-flex"></div>
            <div class="col">
                <div class="d-flex flex-wrap align-items-center mb-3">

                    <template v-if="comment.comments.length > 0 && !isReply">
                        <button
                                v-if="repliesVisible === false"
                                class="btn btn-sm btn-link collapse-toggle collapsed"
                                @click="toggleReplies">
                            {{ __('Show all replies') }} ({{ comment.comments.length }})
                        </button>
                        <button
                                v-if="repliesVisible === true"
                                class="btn btn-sm btn-link collapse-toggle"
                                @click="toggleReplies">
                            {{ __('Hide replies') }}
                        </button>
                    </template>

                    <span v-if="isReply" class="small text-muted">Antwort auf #{{ replyIdentifier}}</span>
                    <button v-if="canReply" class="ml-auto btn btn-sm btn-secondary" @click="showReplyArea">
                        <span>{{ __('Reply Comment') }}</span>
                    </button>
                </div>

                <form
                        v-if="canReply"
                        v-show="replyAreaOpen"
                        @submit.prevent="onSubmit"
                        @keydown="reset">

                    <div class="form-group">
                        <textarea
                                :placeholder="__('Your reply')"
                                id="comment"
                                class="form-control"
                                name="comment"
                                v-model="form.comment"
                                ref="commentArea"
                                :class="{'is-invalid': form.errors.has('comment')}"></textarea>
                        <div class="invalid-feedback" v-if="form.errors.has('comment')" v-text="form.errors.first('comment')"></div>
                    </div>


                    <div class="form-group d-flex justify-content-end">
                        <button type="cancel" @click="hideReplyArea" class="btn btn-link">{{ __('Cancel' )}}</button>

                        <button type="submit" class="btn btn-primary"
                                :disabled="form.errors.any()">{{ __('Reply') }}</button>
                    </div>
                </form>
                <discussion-item
                        v-show="repliesVisible || isReply"
                        v-for="(nestedComment, key) in comment.comments"
                        :main-level="mainLevel"
                        :second-level="(key+1)"
                        :can-reply="canReply"
                        :parent-comment="comment"
                        :parent-id="parentId"
                        :training-id="trainingId"
                        :key="`reply_${nestedComment.id}`"
                        :is-reply="true"
                        :initial-comment="nestedComment"
                ></discussion-item>
            </div>
        </div>
        <hr class="mt-0" v-show="!isReply" />
    </div>
</template>

<script>

import Form from 'form-backend-validation';
import moment from 'moment';
import axios from 'axios';
import autosize from 'autosize';
import trans from '../mixins/trans';

moment.locale('de');

export default {
    mixins: [
        trans
    ],

    props: {
        'mainLevel': Number,
        'secondLevel': Number,
        'parentId': Number,
        'trainingId': Number,
        'parentComment' : Object,
        'initialComment' : Object,
        'isReply' : Boolean,
        'canReply' : Boolean,
    },

    data() {
        return {
            replyAreaOpen: false,
            repliesVisible: false,
            comment: {
                commentator: {},
                comments: []
            },
            form: new Form({
                comment: '',
                parent_id: null,
            }, {
                resetOnSuccess: false,
            })
        };
    },

    filters: {
        formatDate(value) {
            return moment(value).format('DD. MMMM Y')
        }
    },

    mounted() {
        autosize(this.$refs.commentArea);

        this.comment = this.initialComment;
        this.form.parent_id = this.comment.id;
    },

    computed: {

        identifier() {
            if (this.secondLevel) {
                return `${this.mainLevel}.${this.secondLevel}`
            }

            return this.mainLevel;
        },

        replyIdentifier() {
            if (this.isReply) {
                if (this.comment.parent_id === this.parentComment.id) {
                    return this.mainLevel;
                }

                // Find second level
                let secondLevel = 1;
                let index = 1;

                this.parentComment.comments.forEach((comment) => {
                    if (comment.id === this.comment.parent_id) {
                        secondLevel = index;
                    }
                    index++;
                });

                return `${this.mainLevel}.${secondLevel}`
            }

            return null;
        },

    },

    methods: {

        update(comment) {
            this.comment = comment;
        },

        toggleReplies() {
            this.repliesVisible = ! this.repliesVisible;
        },

        showReplyArea() {
            this.replyAreaOpen = true;
        },

        hideReplyArea() {
            this.replyAreaOpen = false;
        },

        reset(event) {
            this.form.errors.clear(event.target.name);
            this.success = false;
        },

        onSubmit(){
            this.form.post(`/api/comments/reply/${this.trainingId}/${this.parentId}`)
                .then(response => {
                    this.form.comment = '';
                    this.success = true;

                    Vue.nextTick(() => {
                        autosize.update(this.$refs.commentArea);
                    });

                    this.hideReplyArea();

                    this.repliesVisible = true;

                    if (this.isReply) {
                        this.$parent.update(response);
                    } else {
                        this.update(response);
                    }
                });
        },
    }
}
</script>